import React, { useEffect, useRef, useState } from "react";
import "./Contact.css";

const FIELDS = [
  { name: "name",    label: "Full Name",        type: "text",  placeholder: "Arjun Sharma" },
  { name: "email",   label: "Email Address",    type: "email", placeholder: "you@example.com" },
  { name: "phone",   label: "Phone Number",     type: "tel",   placeholder: "+1 (987)-345-6969" },
  { name: "service", label: "Service Interested", type: "select", options: [
    "Resume Enhancement",
    "LinkedIn Optimization",
    "Career Counselling",
    "Technical Training",
    "Interview Preparation",
    "Job Placement",
  ]},
  { name: "message", label: "Message",          type: "textarea", placeholder: "Tell us about your career goals..." },
];

const INIT = { name: "", email: "", phone: "", service: "", message: "" };

const Contact = () => {
  const ref = useRef(null);
  const [form, setForm]     = useState(INIT);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError]   = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.08 }
    );
    ref.current?.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setError("Please fill in Name, Email, and Message.");
      return;
    }
    setError("");
    setStatus("loading");
    try {
      // POST to the PHP handler on your Hostinger server
      const res = await fetch("/contact.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, submittedAt: new Date().toISOString() }),
      });
      if (!res.ok) throw new Error("Server error");
      setStatus("success");
      setForm(INIT);
    } catch {
      setStatus("error");
    }
  };

  return (
    <section className="contact" id="contact" ref={ref}>
      <div className="contact__inner">
        {/* Left info */}
        <div className="contact__info reveal">
          <span className="eyebrow">Get In Touch</span>
          <h2 className="contact__h2">
            Your Next Career Move<br />
            <span className="grad-text">Starts With a Conversation</span>
          </h2>
          <p className="contact__sub">
            Ready to transform your career? Fill out the form and our team will
            reach out within 24 hours to understand your goals.
          </p>

          <div className="contact__details">
            <div className="contact__detail-item glass">
              <span className="contact__detail-icon">📍</span>
              <div>
                <div className="contact__detail-label">Office Locations</div>
                <div className="contact__detail-value">Sheridan, Wyoming, 82801, US</div>
                <div className="contact__detail-value">King Ave, Kingsville, Texas 78363, US</div>
              </div>
            </div>
            <div className="contact__detail-item glass">
              <span className="contact__detail-icon">✉️</span>
              <div>
                <div className="contact__detail-label">Email</div>
                <a href="mailto:support@placifyinnovative.com" className="contact__detail-value contact__link">
                  support@placifyinnovative.com
                </a>
              </div>
            </div>
            <div className="contact__detail-item glass">
              <span className="contact__detail-icon">📞</span>
              <div>
                <div className="contact__detail-label">Phone</div>
                <a href="tel:+13612434345" className="contact__detail-value contact__link">
                  +1 (361)-243-4345
                </a>
              </div>
            </div>
            <div className="contact__detail-item glass">
              <span className="contact__detail-icon">🔗</span>
              <div>
                <div className="contact__detail-label">LinkedIn</div>
                <a href="https://www.linkedin.com/company/placify-innovative/" target="_blank" rel="noopener noreferrer" className="contact__detail-value contact__link">
                  Connect on LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="contact__form-wrap reveal reveal-delay-2">
          <div className="contact__form-glass glass">
            {status === "success" ? (
              <div className="contact__success">
                <div className="contact__success-icon">✓</div>
                <h3>Message Sent!</h3>
                <p>Thank you for reaching out. We'll get back to you within 24 hours.</p>
                <button className="contact__success-btn" onClick={() => setStatus("idle")}>
                  Send Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact__form" noValidate>
                <h3 className="contact__form-title">Send us a message</h3>

                <div className="contact__row">
                  {FIELDS.filter(f => f.type !== "textarea" && f.type !== "select").slice(0, 2).map((f) => (
                    <div className="contact__field" key={f.name}>
                      <label className="contact__label">{f.label}</label>
                      <input
                        className="contact__input glass"
                        type={f.type}
                        name={f.name}
                        value={form[f.name]}
                        onChange={handleChange}
                        placeholder={f.placeholder}
                        required={f.name !== "phone"}
                      />
                    </div>
                  ))}
                </div>

                <div className="contact__row">
                  {FIELDS.filter(f => f.name === "phone" || f.name === "service").map((f) => (
                    <div className="contact__field" key={f.name}>
                      <label className="contact__label">{f.label}</label>
                      {f.type === "select" ? (
                        <select
                          className="contact__input contact__select glass"
                          name={f.name}
                          value={form[f.name]}
                          onChange={handleChange}
                        >
                          <option value="">Select a service…</option>
                          {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      ) : (
                        <input
                          className="contact__input glass"
                          type={f.type}
                          name={f.name}
                          value={form[f.name]}
                          onChange={handleChange}
                          placeholder={f.placeholder}
                        />
                      )}
                    </div>
                  ))}
                </div>

                <div className="contact__field">
                  <label className="contact__label">Message</label>
                  <textarea
                    className="contact__input contact__textarea glass"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us about your career goals..."
                    rows={5}
                    required
                  />
                </div>

                {error && <p className="contact__error">{error}</p>}
                {status === "error" && (
                  <p className="contact__error">Something went wrong. Please try again or email us directly.</p>
                )}

                <button
                  type="submit"
                  className="contact__submit"
                  disabled={status === "loading"}
                >
                  {status === "loading" ? (
                    <span className="contact__spinner" />
                  ) : "Send Message →"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
